// Ephemeral in-progress blocks (streaming deltas). The store advances each
// block's `displayedLen` on a rAF tick for a smooth typewriter reveal; we
// render only the revealed slice. Collapsed once the canonical frame lands.

import { useMemo, useState } from 'react';
import type { StreamingBlock } from '../../store/chatStore';
import { AssistantMessage } from './AssistantMessage';
import { parseThinking, thinkingHeading } from './parseThinking';
import { StepRow } from './StepRow';

function Caret() {
  return (
    <span
      aria-hidden="true"
      className="ml-0.5 inline-block h-[1em] w-[2px] -mb-[0.15em] bg-current align-middle animate-cursor-blink"
    />
  );
}

function visibleText(b: StreamingBlock): string {
  return b.displayedLen >= b.buffer.length ? b.buffer : b.buffer.slice(0, b.displayedLen);
}

export function StreamingPreview({ blocks }: { blocks: StreamingBlock[] }) {
  const sorted = useMemo(() => [...blocks].sort((a, b) => a.startedAt - b.startedAt), [blocks]);
  // Streaming step blocks are open by default; the user may collapse one.
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setClosed((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  if (sorted.length === 0) return null;
  const steps = sorted.filter((b) => b.kind !== 'assistant');
  const assistant = sorted.filter((b) => b.kind === 'assistant');

  return (
    <div className="flex flex-col gap-4">
      {steps.length > 0 ? (
        <div className="flex flex-col items-start">
          {steps.map((b) => {
            if (b.kind === 'thinking') {
              const { title, body } = parseThinking(visibleText(b));
              return (
                <StepRow
                  key={b.key}
                  kind="thinking"
                  title={thinkingHeading(title)}
                  status="pending"
                  active
                  open={!closed.has(b.key)}
                  onToggle={() => toggle(b.key)}
                  body={
                    <p className="max-w-[42rem] whitespace-pre-wrap break-words text-[12.5px] leading-relaxed text-text-secondary">
                      {body}
                      <Caret />
                    </p>
                  }
                />
              );
            }
            return (
              <StepRow
                key={b.key}
                kind="tool"
                title="Tool call"
                status="pending"
                active
                open={!closed.has(b.key)}
                onToggle={() => toggle(b.key)}
                body={
                  <pre className="scrollbar-thin max-h-72 max-w-[42rem] overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-elevated px-2.5 py-2 font-mono text-[11.5px] leading-snug text-text-secondary">
                    {visibleText(b)}
                    <Caret />
                  </pre>
                }
              />
            );
          })}
        </div>
      ) : null}
      {assistant.map((b) => (
        <AssistantMessage key={b.key} payload={{ text: visibleText(b) }} streaming />
      ))}
    </div>
  );
}
